import { BotVariables, GlobalStorage } from '@feedyou/schema'
import { isFunction, isUndefined } from 'lodash'

import {
  AdditionalValidationParams,
  ValidatorCriteria,
  ValidatorDefinition
} from './validationSchema'
import { STATIC_READONLY_STORAGE_NAMES } from '@features/storages'
import { isNotEmptyElement, areGivenStringsEqual, Dictionary } from '@feedyou/utils'

// Be careful when to use this. You can only use it if you are 100% sure given validator does not rely on those params and you don't have access to them
export const SKIP_ADDITIONAL_PARAMS = { botVariables: undefined, globalStorages: undefined }

export const evaluateCriteria = (
  criteria: ValidatorCriteria | undefined,
  data: string,
  additionalParams: AdditionalValidationParams
) => {
  if (isUndefined(criteria)) {
    return true
  }

  if (isFunction(criteria)) {
    return criteria(data, additionalParams)
  }

  if (criteria instanceof RegExp) {
    return criteria.test(data)
  }

  return data === criteria
}

export const composeValidationCriteria = (
  criteria: (ValidatorCriteria | undefined)[]
): ValidatorCriteria => {
  return (text, additionalValidationParams) => {
    return criteria
      .filter(isNotEmptyElement)
      .every(criteria => evaluateCriteria(criteria, text, additionalValidationParams))
  }
}

export const findReadonlyStorageMatchingText = (text: string): string | undefined => {
  return STATIC_READONLY_STORAGE_NAMES.find(storageName => areGivenStringsEqual(storageName, text))
}

export const findBotVariableMatchingText = (
  text: string,
  botVariables: BotVariables = {}
): string | undefined => {
  return Object.keys(botVariables).find(botVariableName =>
    areGivenStringsEqual(botVariableName, text)
  )
}

export const composeValidators = (validators: ValidatorDefinition[]): ValidatorDefinition => {
  return {
    criteria: composeValidationCriteria(validators.map(validator => validator.criteria)),

    getErrorMessage: (validatedText, additionalValidationParams) => {
      for (const validator of validators) {
        if (!evaluateCriteria(validator.criteria, validatedText, additionalValidationParams)) {
          return validator.getErrorMessage(validatedText, additionalValidationParams)
        }
      }

      return ''
    }
  }
}

export const isReadonlyGlobalStorage = (
  text: string,
  globalStorages: Dictionary<string, GlobalStorage> | undefined
) => globalStorages?.[text]?.readonly
